import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { config } from '../config/env';
import { useAuthenticatedRequest } from '../lib/useAuthenticatedRequest';
import { AccountRequestError, accountRequestMessage } from '../lib/accountRequest';
import { signInPathForDestination } from '../lib/authRedirect';

const readError = async (response: Response) => {
    if (response.status === 401) throw new AccountRequestError('authentication', 'Please sign in to access your guide.');
    const body = await response.json().catch(() => ({}));
    throw new AccountRequestError('request', typeof body.detail === 'string' ? body.detail : 'We could not access your guide. Please try again or contact hello@topclip.ai.');
  };

type Props = { sessionId?: string | null; paymentReturn?: boolean };
export const GuideDownload: React.FC<Props> = props => {
  const { user } = useAuth();
  return <GuideDownloadForOwner key={user?.id ?? 'signed-out'} {...props} />;
};

const GuideDownloadForOwner: React.FC<Props> = ({ sessionId, paymentReturn = false }) => {
  const { user } = useAuth();
  const request = useAuthenticatedRequest();
  const [owned, setOwned] = useState(false);
  const [checking, setChecking] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [needsSignIn, setNeedsSignIn] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const downloadingRef = useRef(false);
  const query = sessionId ? `?session_id=${encodeURIComponent(sessionId)}` : '';
  const endpoint = (name: string) => `${config.API_BASE_URL}/api/stripe/${name}${query}`;


  useEffect(() => {
    let current = true;
    setChecking(true);
    setError(null);
    setNeedsSignIn(false);
    const check = async () => {
      try {
        await request(async ({ accessToken, signal, assertCurrent }) => {
          const response = await fetch(`${config.API_BASE_URL}/api/stripe/guide-access${query}`, { headers: { Authorization: `Bearer ${accessToken}` }, signal });
          if (!response.ok) await readError(response);
          const body = await response.json();
          assertCurrent();
          if (typeof body.owned !== 'boolean') throw new Error('Invalid guide access response');
          if (current) setOwned(body.owned);
        });
      } catch (problem) {
        if (current && !(problem instanceof AccountRequestError && problem.kind === 'canceled')) {
          setError(accountRequestMessage(problem));
          setNeedsSignIn(problem instanceof AccountRequestError && problem.kind === 'authentication');
        }
      } finally { if (current) setChecking(false); }
    };
    void check();
    return () => { current = false; };
  }, [request, query, attempt]);

  const download = async () => {
    if (downloadingRef.current) return;
    downloadingRef.current = true;
    setDownloading(true);
    setError(null);
    try {
      await request(async ({ accessToken, signal, assertCurrent }) => {
        const response = await fetch(endpoint('guide-download'), { headers: { Authorization: `Bearer ${accessToken}` }, signal });
        if (!response.ok) await readError(response);
        if (!response.headers.get('content-type')?.includes('application/pdf')) throw new Error('Invalid download');
        const blob = await response.blob();
        assertCurrent();
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'The TopClip Faceless Content Playbook.pdf';
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      });
    } catch (problem) {
      if (!(problem instanceof AccountRequestError && problem.kind === 'canceled')) {
        setError(accountRequestMessage(problem));
        setNeedsSignIn(problem instanceof AccountRequestError && problem.kind === 'authentication');
      }
    } finally { downloadingRef.current = false; setDownloading(false); }
  };

  if (!paymentReturn && (!user || (checking && !owned) || (!owned && !error))) return null;
  return <section className="my-4 rounded-xl border border-blue-100 bg-blue-50 p-4 text-center" aria-label="Your PDF guide">
    <h3 className="font-semibold text-gray-900">The TopClip Faceless Content Playbook</h3>
    {checking ? <p role="status" className="mt-2 text-sm">Checking your guide purchase...</p> : owned ? <>
      <p className="mt-2 text-sm text-gray-600">Your 18-page guide is ready. You can download it again from Account anytime.</p>
      <button type="button" onClick={() => void download()} disabled={downloading} className="mt-3 min-h-12 rounded-xl bg-blue-600 px-5 py-3 font-semibold text-white disabled:opacity-60">{downloading ? 'Preparing download...' : 'Download PDF'}</button>
    </> : paymentReturn && !error ? <p className="mt-2 text-sm">Your guide purchase has not been confirmed yet. If you just paid, check again in a moment. You do not need to buy again.</p> : null}
    {error && <p role="alert" className="mt-2 text-sm text-red-700">{error}</p>}
    {!checking && !owned && !needsSignIn && <button type="button" className="mt-2 min-h-11 text-blue-700 underline" onClick={() => setAttempt(value => value + 1)}>Check purchase again</button>}
    {needsSignIn && <a className="block min-h-11 py-3 text-blue-700 underline" href={signInPathForDestination('/dashboard')}>Sign in again</a>}
    {(error || (!checking && !owned)) && <a className="block min-h-11 py-3 text-blue-700 underline" href="mailto:hello@topclip.ai?subject=TopClip%20PDF%20download%20help">Contact support</a>}
  </section>;
};
